import { DateTime } from 'luxon';
import * as R from 'ramda';
import React from 'react';
import styled from 'styled-components';

import { Icon } from '@ui/icons/Icon.tsx';
import Link from '@ui/Link.tsx';
import { formatTime } from '@utils/date.ts';

import { useCSSColors } from '../../../../../src/hooks/useCSSColors.ts';

const weekdays = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];

const Wrapper = styled.div`
  margin: 16px -12px 0;
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid var(--color-primaryText-10);
`;

// Note: Is this really unique to the Information section? It feels this should be pulled out to be usable in other sections for consistency.
const Title = styled.h2`
  && {
    margin: 0px;
    background: var(--color-primary-05);
    ${({ theme }) => theme.fontSize('14px')};
    line-height: 18px;
    font-weight: 500;
    padding: 7px 8px;
    border-top: 1px solid var(--color-primaryText-10);
    text-transform: uppercase;
    text-align: center;
  }
`;

const InformationsList = styled.ul`
  ${({ theme }) => theme.fontSize('16px')};
  line-height: 150%;
  margin: 0;
  padding: 0;
  list-style: none;
`;

const IconWrapper = styled.div`
  margin-inline-end: 16px;
  flex: none;
  flex: 0 0 auto;
`;

const Information = styled.li`
  padding: 12px 16px;
  border-top: 1px solid var(--color-primaryText-10);
  display: flex;
  align-items: center;
  flex-wrap: wrap;

  > :not(${IconWrapper}) {
    flex: 1 1 auto;
    min-width: 0;
  }

  a {
    text-decoration: none;
    color: #333;
    cursor: pointer;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }
`;

const InfoText = styled.span`
  overflow-wrap: anywhere;
  word-break: break-word;
`;

const WebsiteUrl = styled.span`
  display: block;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding: 4px;
  margin: -4px;
`;
const OpeningTime = styled.span`
  text-transform: capitalize;
  display: block;
`;

const OpeningTimes = styled.span`
  display: flex;
  flex-direction: column;
`;

// Takes a time in HH:MM format and reformats it for the specified locale
const reformatTime = (t, locale) => {
  let [hours, minutes] = t.split(':').map(Number);
  hours %= 24;
  minutes %= 60;

  return formatTime(DateTime.fromObject({ hour: hours, minute: minutes }), locale);
};

const PoiInformation = ({ poi, T, lang, submitAnalyticsEvent, submitAnalyticsEventWithAppInsights }) => {
  const colors = useCSSColors();
  if (!poi) {
    return null;
  }

  const info = R.pick(['phone', 'phoneExt', 'operationHours', 'roomInfo'], poi);

  R.pipe(
    R.propOr([], 'links'),
    R.find(R.propEq('primary', 'type')),
    R.prop('url'),
    R.unless(R.isNil, url => {
      info.url = url;
    }),
  )(poi);

  if (R.isEmpty(info)) {
    return null;
  }

  const getOpeningHours = operationHours => {
    if (!operationHours) {
      return null;
    }

    if (poi.operationHours === 'Mo-Su 00:00-24:00') {
      return T('poiView:Open 24 hours');
    }

    const formatTimeToLocale = (t, locale) => (t === 'lastFlight' ? T('poiView:Last Flight') : reformatTime(t, locale));

    // another hack to parse out the times.. <sigh>
    const splitDayHours = time => {
      const firstSpace = time.indexOf(' ');
      return [time.substring(0, firstSpace), time.substring(firstSpace + 1)];
    };

    let hours = operationHours.split(';').map(hourStr => {
      const [day, time] = hourStr.trim().split(' ');
      const newTime = time
        .split('-')
        .map(t => formatTimeToLocale(t, lang))
        .join('-');
      return `${day} ${newTime}`;
    });

    if (lang !== 'en') {
      hours = hours.filter(Boolean).map(time => {
        // get separate time frames
        const arr = splitDayHours(time); // separate day from opening hours
        const days = arr[0].split('-'); // if day was in Mo-Fr format, separate it
        const openingHours = arr.slice(1, arr.length);
        const translatedDays = days.map(day => {
          const dayOfWeek = weekdays.findIndex(el => el === day); // get day of the week
          const currentDay = new Date();
          const distance = dayOfWeek - currentDay.getDay(); // check the difference between day and the current day of the week
          const newDate = currentDay.setDate(currentDay.getDate() + distance);
          const newDay = DateTime.fromMillis(newDate).setLocale(lang).weekdayShort; // get localized day of the week
          return newDay;
        });
        return translatedDays.join('-') + ' ' + openingHours;
      });
    }

    return hours;
  };

  const getTitle = category => {
    if (category.startsWith('workstation')) {
      return 'Contact';
    }
    if (category.startsWith('meeting')) {
      return 'Room Info';
    }
    return 'Information';
  };
  const formattedHours = getOpeningHours(poi.operationHours);
  const sendInsightsEvent = () => {
    submitAnalyticsEventWithAppInsights(poi, 'website', info.url, {
      poiCategory: poi.category,
    });
  };

  return (
    <Wrapper data-testid="PoiInformation">
      <Title>{T(`poiView:${getTitle(poi.category)}`)}</Title>
      <InformationsList>
        {info.url && (
          <Information>
            <IconWrapper>
              <Icon
                width={32}
                height={32}
                cdn
                name={poi.category.startsWith('workstation') ? 'email' : 'website'}
                fillColor={colors.primaryText}
              />
            </IconWrapper>
            <WebsiteUrl>
              <Link
                data-testid="WebsiteUrl"
                href={poi.category.startsWith('workstation') ? `mailto:${info.url}` : info.url}
                onClick={sendInsightsEvent}
              >
                {info.url}
              </Link>
            </WebsiteUrl>
          </Information>
        )}

        {info.phone && (
          <Information>
            <IconWrapper>
              <Icon
                width={32}
                height={32}
                cdn
                name="phone"
                fillColor={colors.primaryText}
                role="img"
                aria-label={T('poiView:Phone Number')}
              />
            </IconWrapper>
            {info.phoneExt ? (
              <Link
                href={`tel:${info.phone},${info.phoneExt}`}
                onClick={() => submitAnalyticsEvent(poi, 'phone', info.phone + ' ext ' + info.phoneExt)}
              >
                <span dir="ltr">
                  {T('poiView:Phone Number with Extension', { phone: info.phone, extension: info.phoneExt })}
                </span>
              </Link>
            ) : (
              <Link href={`tel:${info.phone}`} onClick={() => submitAnalyticsEvent(poi, 'phone', info.phone)}>
                {info.phone}
              </Link>
            )}
          </Information>
        )}

        {info.operationHours && (
          <Information>
            <IconWrapper>
              <Icon
                width={32}
                height={32}
                cdn
                name="opening-hours"
                fillColor={colors.primaryText}
                role="img"
                aria-label={T('poiView:Hours')}
              />
            </IconWrapper>
            {Array.isArray(formattedHours) ? (
              <OpeningTimes>
                {formattedHours.map((hours, index) => (
                  <OpeningTime key={index} tabIndex={0}>
                    {hours}
                  </OpeningTime>
                ))}
              </OpeningTimes>
            ) : (
              <InfoText aria-label={T('poiView:Operational Hours')} tabIndex={0}>
                {formattedHours}
              </InfoText>
            )}
          </Information>
        )}

        {info.roomInfo &&
          info.roomInfo.map(el => (
            <Information key={el.svgId}>
              <IconWrapper>
                <Icon width={32} height={32} name={el.svgId} cdn fillColor={colors.primaryText} />
              </IconWrapper>
              <InfoText>{el.name}</InfoText>
            </Information>
          ))}
      </InformationsList>
    </Wrapper>
  );
};

export default PoiInformation;
export { reformatTime };
